/**
 * JsSIP UA 封装 — 注册 / 拨号 / 挂断 / 接听 / 事件订阅
 *
 * 单例: 整个应用共享一个 UA, 顶部导航的 SipPhone 通过 sipService.subscribe()
 * 拿到状态变化.
 *
 * 注意: 浏览器必须运行在安全上下文 (https 或 localhost) 才能拿到 getUserMedia.
 */
import JsSIP from 'jssip';
import type { RTCSession } from 'jssip/lib/RTCSession';
import type { IncomingRTCSessionEvent, OutgoingRTCSessionEvent } from 'jssip/lib/UA';
import {
  DEFAULT_SIP_CONFIG,
  buildSipUri,
  buildWsUri,
  getUserExtension,
  sipConfigStore,
} from './sip-config.js';

JsSIP.debug.disable();

export type SipRegistrationState = 'idle' | 'connecting' | 'registered' | 'failed' | 'disconnected';
export type SipCallState =
  | 'idle'
  | 'calling'
  | 'ringing'
  | 'incoming'
  | 'in-call'
  | 'ended'
  | 'failed';

export interface SipState {
  registration: SipRegistrationState;
  call: SipCallState;
  remote: string | null;
  errorMessage: string | null;
  startedAt: number | null;
  /** FreeSWITCH 会议桥线路状态. 浏览器只保持一条到 conference bridge 的 SIP session. */
  conferenceLines: ConferenceLine[];
}

export interface ConferenceLine {
  id: string;
  remote: string;
  state: 'calling' | 'in-call' | 'ended' | 'failed';
}

/** 通话生命周期事件 (供 SipPhone 自动写 call-log) */
export interface SipCallEvent {
  direction: 'outbound' | 'inbound';
  remote: string;
  startAt?: number;
  endedAt: number;
  durationSec: number;
  status: 'completed' | 'missed' | 'failed' | 'cancelled';
  failureReason?: string;
}
type CallEventListener = (ev: SipCallEvent) => void;
const callEventListeners = new Set<CallEventListener>();

type Listener = (state: SipState) => void;

const listeners = new Set<Listener>();

let ua: JsSIP.UA | null = null;
let currentSession: RTCSession | null = null;
let audioEl: HTMLAudioElement | null = null;
let currentUserId: string | null = null;
let currentExtension: string | null = null;
let currentRegistrationSignature: string | null = null;
let lifecycleId = 0;
const audioBoundConnections = new WeakSet<RTCPeerConnection>();

let state: SipState = {
  registration: 'idle',
  call: 'idle',
  remote: null,
  errorMessage: null,
  startedAt: null,
  conferenceLines: [],
};

// 当前通话的方向/起始时间, ended/failed 时用于发出 SipCallEvent
let activeCallContext: {
  direction: 'outbound' | 'inbound';
  remote: string;
  startAt: number;
  answered: boolean;
} | null = null;

// 会议桥模式 — 单条 SIP session 呼叫 FreeSWITCH conference bridge
let conferenceBridgeSession: RTCSession | null = null;
let conferenceActive = false;

function emitCallEvent(ev: SipCallEvent) {
  for (const fn of callEventListeners) fn(ev);
}

function ensureAudio(): HTMLAudioElement {
  if (audioEl) return audioEl;
  audioEl = document.createElement('audio');
  audioEl.autoplay = true;
  audioEl.style.display = 'none';
  document.body.appendChild(audioEl);
  return audioEl;
}

function playRemoteStream(stream: MediaStream) {
  const el = ensureAudio();
  el.srcObject = stream;
  el.play().catch(() => {
    // 用户未交互时可能被浏览器拦截, 拨号/接听按钮触发故通常 ok
  });
}

function updateState(patch: Partial<SipState>) {
  state = { ...state, ...patch };
  for (const fn of listeners) fn(state);
}

function attachStreamToAudio(session: RTCSession) {
  const pc = session.connection;
  if (!pc) return;

  if (!audioBoundConnections.has(pc)) {
    audioBoundConnections.add(pc);
    pc.addEventListener('track', (ev) => {
      const stream = ev.streams[0] ?? new MediaStream([ev.track]);
      if (stream) playRemoteStream(stream);
    });
  }

  const remoteAudioTracks = pc
    .getReceivers()
    .map((receiver) => receiver.track)
    .filter(
      (track): track is MediaStreamTrack => track?.kind === 'audio' && track.readyState !== 'ended',
    );
  if (remoteAudioTracks.length > 0) {
    playRemoteStream(new MediaStream(remoteAudioTracks));
  }
}

function scheduleAttachStreamToAudio(session: RTCSession) {
  attachStreamToAudio(session);
  const retry = () => {
    if (currentSession === session) {
      attachStreamToAudio(session);
    }
  };
  window.setTimeout(retry, 0);
  window.setTimeout(retry, 300);
}

function bindSessionEvents(session: RTCSession, remote: string, outgoing: boolean) {
  currentSession = session;
  scheduleAttachStreamToAudio(session);
  // 记录上下文供 ended/failed 时计算 duration + status
  activeCallContext = {
    direction: outgoing ? 'outbound' : 'inbound',
    remote,
    startAt: Date.now(),
    answered: false,
  };

  session.on('progress', () => {
    updateState({ call: outgoing ? 'ringing' : 'incoming', remote });
  });
  session.on('accepted', () => {
    scheduleAttachStreamToAudio(session);
    if (activeCallContext) {
      activeCallContext.answered = true;
      activeCallContext.startAt = Date.now();
    }
    updateState({ call: 'in-call', remote, startedAt: Date.now() });
  });
  session.on('confirmed', () => {
    scheduleAttachStreamToAudio(session);
    updateState({ call: 'in-call', remote, startedAt: state.startedAt ?? Date.now() });
  });
  session.on('ended', () => {
    const ctx = activeCallContext;
    activeCallContext = null;
    currentSession = null;
    if (ctx) {
      const endedAt = Date.now();
      emitCallEvent({
        direction: ctx.direction,
        remote: ctx.remote,
        startAt: ctx.startAt,
        endedAt,
        durationSec: ctx.answered ? Math.round((endedAt - ctx.startAt) / 1000) : 0,
        status: ctx.answered ? 'completed' : 'cancelled',
      });
    }
    updateState({ call: 'ended', remote: null, startedAt: null });
    setTimeout(() => {
      if (state.call === 'ended') updateState({ call: 'idle' });
    }, 2000);
  });
  session.on('failed', (data: { cause?: string }) => {
    const ctx = activeCallContext;
    activeCallContext = null;
    currentSession = null;
    const endedAt = Date.now();
    if (ctx) {
      // 入向未接 → missed; 出向取消/失败 → failed
      const status =
        ctx.direction === 'inbound' && !ctx.answered
          ? 'missed'
          : ctx.answered
            ? 'failed'
            : 'failed';
      emitCallEvent({
        direction: ctx.direction,
        remote: ctx.remote,
        startAt: ctx.startAt,
        endedAt,
        durationSec: ctx.answered ? Math.round((endedAt - ctx.startAt) / 1000) : 0,
        status,
        failureReason: data?.cause,
      });
    }
    updateState({
      call: 'failed',
      remote: null,
      startedAt: null,
      errorMessage: data?.cause ?? '呼叫失败',
    });
    setTimeout(() => {
      if (state.call === 'failed') updateState({ call: 'idle', errorMessage: null });
    }, 3000);
  });
}

export const sipService = {
  getState(): SipState {
    return state;
  },

  subscribe(fn: Listener): () => void {
    listeners.add(fn);
    fn(state);
    return () => listeners.delete(fn);
  },

  /** 订阅每次通话结束事件 — 用于自动写 call-log 等 */
  subscribeCallEvents(fn: CallEventListener): () => void {
    callEventListeners.add(fn);
    return () => callEventListeners.delete(fn);
  },

  isReady(): boolean {
    return state.registration === 'registered';
  },

  /** 是否处于会议模式 */
  inConference(): boolean {
    return conferenceActive;
  },

  /** 退出会议模式 — 挂断 FreeSWITCH conference bridge */
  endConference() {
    if (conferenceBridgeSession) {
      try {
        conferenceBridgeSession.terminate();
      } catch {
        /* ignore */
      }
    }
    conferenceBridgeSession = null;
    conferenceActive = false;
    updateState({ conferenceLines: [], call: 'idle', remote: null });
  },

  /** 加入 FreeSWITCH 会议桥: sip:conf_<roomCode>@realm */
  joinConferenceBridge(roomCode: string): { ok: boolean; reason?: string; uri?: string } {
    if (!ua || state.registration !== 'registered') {
      return { ok: false, reason: '尚未注册到 SIP 服务器' };
    }
    const code = roomCode.trim().toUpperCase();
    if (!code) return { ok: false, reason: '会议房间码为空' };
    if (conferenceBridgeSession) {
      try {
        conferenceBridgeSession.terminate();
      } catch {
        /* ignore */
      }
      conferenceBridgeSession = null;
    }
    const cfg = sipConfigStore.get();
    const bridgeNumber = `conf_${code}`;
    const bridgeUri = `sip:${bridgeNumber}@${cfg.realm || cfg.server}`;
    try {
      const session = ua.call(bridgeUri, {
        mediaConstraints: { audio: true, video: false },
        rtcOfferConstraints: { offerToReceiveAudio: true, offerToReceiveVideo: false },
      });
      conferenceBridgeSession = session;
      conferenceActive = true;
      bindSessionEvents(session, bridgeNumber, true);
      const updateBridge = (lineState: ConferenceLine['state']) => {
        if (conferenceBridgeSession !== session) return;
        if (lineState === 'ended' || lineState === 'failed') {
          conferenceBridgeSession = null;
          conferenceActive = false;
          updateState({ conferenceLines: [] });
          return;
        }
        updateState({
          conferenceLines: [{ id: `bridge_${code}`, remote: bridgeNumber, state: lineState }],
        });
      };
      updateState({
        conferenceLines: [{ id: `bridge_${code}`, remote: bridgeNumber, state: 'calling' }],
      });
      session.on('accepted', () => updateBridge('in-call'));
      session.on('confirmed', () => updateBridge('in-call'));
      session.on('ended', () => updateBridge('ended'));
      session.on('failed', () => updateBridge('failed'));
      return { ok: true, uri: bridgeUri };
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      conferenceBridgeSession = null;
      conferenceActive = false;
      updateState({ conferenceLines: [] });
      return { ok: false, reason: message };
    }
  },

  /** 会议桥模式不再由浏览器分别呼叫每个成员. */
  dialConferenceLine(target: string): { ok: boolean; reason?: string } {
    const clean = target.replace(/\s+/g, '').trim();
    return {
      ok: false,
      reason: clean ? '会议桥模式下请通过邀请通知成员加入会议' : '会议桥模式下无需手动拨入成员',
    };
  },

  /** 挂断会议桥 */
  hangupConferenceLine(lineId: string) {
    if (!conferenceBridgeSession || !lineId.startsWith('bridge_')) return;
    try {
      conferenceBridgeSession.terminate();
    } catch {
      /* ignore */
    }
    conferenceBridgeSession = null;
    conferenceActive = false;
    updateState({ conferenceLines: [] });
  },

  /**
   * 初始化 / 注册 — 传入 userId 决定使用哪个分机号.
   * 若已用相同 userId 注册, 直接返回; 配置变化时会自动重连.
   */
  start(userId: string) {
    const cfg = sipConfigStore.get();
    if (!cfg.enabled) {
      updateState({ registration: 'idle' });
      return;
    }
    const extension = getUserExtension(userId);
    const registrationSignature = buildRegistrationSignature(cfg, extension);
    // 已用同一 userId + 同一分机号 + 同一配置注册或注册中, 跳过.
    if (
      ua &&
      currentUserId === userId &&
      currentExtension === extension &&
      currentRegistrationSignature === registrationSignature &&
      (state.registration === 'connecting' || state.registration === 'registered')
    ) {
      return;
    }

    this.stop();
    const startLifecycleId = ++lifecycleId;
    currentUserId = userId;
    currentExtension = extension;
    currentRegistrationSignature = registrationSignature;

    try {
      const wsUri = buildWsUri(cfg);
      const sipUri = buildSipUri(extension, cfg);
      const socket = new JsSIP.WebSocketInterface(wsUri);
      if (shouldForceWsViaTransport(cfg)) {
        socket.via_transport = 'WS';
      }

      const nextUa = new JsSIP.UA({
        sockets: [socket],
        uri: sipUri,
        password: cfg.password,
        register: true,
        session_timers: false,
        user_agent: 'Crest-CRM SIP/1.0',
      });
      ua = nextUa;

      const updateIfCurrent = (patch: Partial<SipState>) => {
        if (lifecycleId === startLifecycleId && ua === nextUa) updateState(patch);
      };

      nextUa.on('connecting', () =>
        updateIfCurrent({ registration: 'connecting', errorMessage: null }),
      );
      nextUa.on('connected', () => updateIfCurrent({ registration: 'connecting' }));
      nextUa.on('disconnected', () => updateIfCurrent({ registration: 'disconnected' }));
      nextUa.on('registered', () =>
        updateIfCurrent({ registration: 'registered', errorMessage: null }),
      );
      nextUa.on('unregistered', () => updateIfCurrent({ registration: 'disconnected' }));
      nextUa.on('registrationFailed', (data: { cause?: string }) => {
        updateIfCurrent({
          registration: 'failed',
          errorMessage: data?.cause ?? '注册失败',
        });
      });
      nextUa.on('newRTCSession', (e: IncomingRTCSessionEvent | OutgoingRTCSessionEvent) => {
        if (lifecycleId !== startLifecycleId || ua !== nextUa) return;
        const session = e.session;
        const outgoing = e.originator === 'local';
        const remote = (session.remote_identity?.uri?.user as string | undefined) ?? '未知号码';
        bindSessionEvents(session, remote, outgoing);
        if (!outgoing) {
          updateState({ call: 'incoming', remote });
        }
      });

      nextUa.start();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      if (lifecycleId === startLifecycleId) {
        updateState({ registration: 'failed', errorMessage: message });
      }
    }
  },

  /** 停止注册 / 释放 UA */
  stop() {
    lifecycleId += 1;
    const stoppingUa = ua;
    ua = null;
    if (currentSession) {
      try {
        currentSession.terminate();
      } catch {
        /* ignore */
      }
      currentSession = null;
    }
    if (stoppingUa) {
      try {
        stoppingUa.stop();
      } catch {
        /* ignore */
      }
    }
    currentUserId = null;
    currentExtension = null;
    currentRegistrationSignature = null;
    updateState({
      registration: 'idle',
      call: 'idle',
      remote: null,
      startedAt: null,
      errorMessage: null,
    });
  },

  /** 重启注册 — 配置变化时调用 */
  restart() {
    const uid = currentUserId;
    this.stop();
    if (uid) this.start(uid);
  },

  /** 拨号 */
  dial(target: string): { ok: boolean; reason?: string } {
    if (!ua || state.registration !== 'registered') {
      return { ok: false, reason: '尚未注册到 SIP 服务器' };
    }
    if (currentSession) {
      return { ok: false, reason: '当前已有通话, 请先挂断' };
    }
    const clean = target.replace(/\s+/g, '').trim();
    if (!clean) return { ok: false, reason: '请输入目标号码' };
    const cfg = sipConfigStore.get();
    const targetUri = clean.startsWith('sip:') ? clean : `sip:${clean}@${cfg.realm || cfg.server}`;
    try {
      ua.call(targetUri, {
        mediaConstraints: { audio: true, video: false },
        rtcOfferConstraints: { offerToReceiveAudio: true, offerToReceiveVideo: false },
      });
      updateState({ call: 'calling', remote: clean, errorMessage: null });
      return { ok: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      updateState({ call: 'failed', errorMessage: message });
      return { ok: false, reason: message };
    }
  },

  /** 接听来电 */
  answer() {
    if (!currentSession) return;
    try {
      currentSession.answer({
        mediaConstraints: { audio: true, video: false },
      });
      scheduleAttachStreamToAudio(currentSession);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      updateState({ call: 'failed', errorMessage: message });
    }
  },

  /** 挂断 */
  hangup() {
    if (currentSession) {
      try {
        currentSession.terminate();
      } catch {
        /* ignore */
      }
    }
    updateState({ call: 'idle', remote: null, startedAt: null });
  },

  /** DTMF 按键 */
  sendDtmf(tone: string) {
    if (currentSession) {
      try {
        currentSession.sendDTMF(tone);
      } catch {
        /* ignore */
      }
    }
  },
};

function shouldForceWsViaTransport(cfg: ReturnType<typeof sipConfigStore.get>): boolean {
  return (
    cfg.secure &&
    cfg.server === DEFAULT_SIP_CONFIG.server &&
    cfg.port === DEFAULT_SIP_CONFIG.port &&
    cfg.wsPath === DEFAULT_SIP_CONFIG.wsPath
  );
}

function buildRegistrationSignature(
  cfg: ReturnType<typeof sipConfigStore.get>,
  extension: string,
): string {
  return JSON.stringify({
    wsUri: buildWsUri(cfg),
    sipUri: buildSipUri(extension, cfg),
    password: cfg.password,
    enabled: cfg.enabled,
  });
}
